import React, { useState, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import Engine from '../../classes/Engine';
import Button from '../layout/Button';
import ModalManager from '../layout/ModalManager';
import { CustomEventTypes, DndItemTypes, SaveModificationTypes } from '../../globals';
import Spacer from '../layout/Spacer';
import { ClearIcon, PingIcon } from '../Iconset';
import SelectObjectsAction from '../../actions/SelectObjectsAction';
import ProjectSaver from '../../classes/ProjectSaver';
import SceneObjectUtils from '../../classes/utilities/SceneObjectUtils';
import Scenario from '../../classes/scenarioElements/Scenario';
import ARTrackerPicker from '../trackersUI/ARTrackerPicker';
import useEventListener from '../reactUtils/useEventListener';
import AnalyticsManager from '../../classes/AnalyticsManager';

interface IARTrackerObjectPropertyProps extends IBasePropertyProps
{
	/** hide all the extra buttons */
	hideButtons?: boolean;
	/** hide the button that selects the tracker in the trackers list */
	hideHighlightButton?: boolean;
	/** hide the button that clears the value of the property */
	hideClearButton?: boolean;
}

export default function ARTrackerObjectProperty(props: IARTrackerObjectPropertyProps)
{
	const currentScene = Scenario.currentStep()?.scene ?? Engine.getInstance().getCurrentScene() ?? null;
	if(!currentScene) return;

	useEventListener(CustomEventTypes.updatePropertiesComponent, handleSceneHierarchyChanged, document);

	const sceneObject = props.value ? SceneObjectUtils.findObjectByGuid(currentScene, props.value) : null;
	const [faceValue, setFaceValue] = useState(props.multivalues ? "(Multiple Values)" : sceneObject ? sceneObject.name : "Select Object");

	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-scene-object-property";

	const [{ canDrop }, drop] = useDrop({
		accept: [
			DndItemTypes.TRACKER_TREE_ITEM,
		],
		collect: monitor => ({
			hovered: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop(),
		}),
		drop: (item: any, monitor) =>
		{
			if (!monitor.didDrop())
			{
				onObjectChosen(item.selected[0].props.nodeId);
			}
		},
	});

	useEffect(() =>
	{
		handleSceneHierarchyChanged();
	}, [props.value]);

	function onObjectChosen(objectId: string)
	{
		const object = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(), objectId);
		if(object)
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Reference);
		}

		props.onChangeCallback?.(objectId);
		props.onCommitCallback?.(objectId);
	}

	function handleSceneHierarchyChanged()
	{
		if(sceneObject)
		{
			setFaceValue(sceneObject.userData.removedFromScene ? "Select Object" : sceneObject.name);
		}
		else if(props.value)
		{
			const object = SceneObjectUtils.findObjectByGuid(currentScene!, props.value);
			setFaceValue(object ? object.name : "Select Object");
		}		
		else
		{
			setFaceValue("Select Object");
		}
	}

	return drop(
		<div className={"objectPropertyWrapper"}>
			<PropertyBase {...props}
				data-name={dataName + "-root"}
				highlight={canDrop}
			>
				<Button
					data-name={dataName + "-button"}
					silent={true}
					label={faceValue}
					onClick={(e: any) =>
					{
						ModalManager.addModal({
							content:
								<ARTrackerPicker
									data-name={dataName + "-picker"}
									value={props.value}
									onSelectedCallback={(id) =>
									{
										props.onChangeCallback?.(id);
										
										if (props.onCommitCallback)
										{
											AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target?.dataset?.name);
											props.onCommitCallback(id);
										}
									}}
								/>
						});
					}}
				/>
				<Spacer size={{ x: "2px" }} />
				{
					props.hideButtons || props.hideHighlightButton || props.readOnly ? null :
						<PingIcon scale={0.7}
							cellclass={"propertySmallCell"}
							data-name={dataName + "-select-value-button"}
							onClick={() =>
							{
								if(sceneObject) new SelectObjectsAction([sceneObject], Engine.getInstance().getPickingManager().getCurrentSelection().objects);
							}}
						/>
				}
				{
					props.hideButtons || props.hideClearButton || props.readOnly ? null :
						<ClearIcon scale={0.7}
							cellclass={"propertySmallCell"}
							data-name={dataName + "-clear-value-button"}
							onClick={() => onObjectChosen("")}
						/>
				}
			</PropertyBase>
		</div>
	);
}